import { Card, CardContent } from "@/components/ui/card";
import { NotifyMeButton } from "@/components/NotifyMe";
import { dimsLine } from "@/lib/dims";

export const FORMAT_CHIPS = [["4x4", "4×4"], ["2x6", "2×6"], ["2x2", "2×2"]];

export const compositionText = (v) => (v.composition || []).map((c) => `${c.us_name || c.moroccan_name}${c.share_pct ? ` ${c.share_pct}%` : ""}`).join(" · ");

function Availability({ v, customerView, notifyMe, onNotify }) {
  return (
    <div className="mt-4 flex flex-wrap items-center gap-2">
      {v.florida_available > 0 ? (
        <span className="badge-status badge-available" data-testid={`florida-badge-${v.sku}`}>In stock · {v.florida_available} boxes</span>
      ) : (
        <span className="badge-status badge-neutral" data-testid={`florida-badge-${v.sku}`}>{v.incoming_available > 0 ? "None in Florida — incoming" : "None in Florida"}</span>
      )}
      {v.incoming_available > 0 && <span className="badge-status badge-reserved" data-testid={`incoming-badge-${v.sku}`}>Incoming: {v.incoming_available}</span>}
      {!customerView && !v.quote_required && !(v.florida_available > 0) && (
        <NotifyMeButton variant={v} active={notifyMe.active.has(v.id)} onClick={onNotify} className="h-7 px-2 text-xs" />
      )}
    </div>
  );
}

function Prices({ v, customerView }) {
  const money = (n) => `$${n.toFixed(2)}`;
  return (
    <div className="mt-4 space-y-1 border-t border-border pt-4 text-sm">
      {v.retail_price != null ? (
        <div className="flex justify-between">
          <span className="text-muted-foreground">{customerView ? "Price" : "My selling price ($/sq ft)"}</span>
          <span className="num text-charcoal" data-testid={`retail-per-sqft-${v.sku}`}>
            {v.retail_price_per_sqft != null ? <><span className="text-base font-semibold">{money(v.retail_price_per_sqft)}</span><span className="text-xs text-muted-foreground">/sq ft</span></> : money(v.retail_price)}
          </span>
        </div>
      ) : !customerView && (
        <div className="flex justify-between" data-testid={`retail-unset-${v.sku}`}><span className="text-muted-foreground">My selling price ($/sq ft)</span><span className="text-xs text-terracotta">Set price per square foot</span></div>
      )}
      {!customerView && (
        <>
          <div className="flex justify-between" data-testid={`trade-price-row-${v.sku}`}>
            <span className="text-muted-foreground">Your trade price</span>
            {v.quote_required ? (
              <span className="text-xs font-medium text-terracotta" data-testid={`quote-required-${v.sku}`}>{v.quote_message || "Contact us for pricing"}</span>
            ) : v.trade_price != null ? (
              <span className="text-charcoal" data-testid={`trade-per-sqft-${v.sku}`}>
                {v.trade_price_per_sqft != null && <><span className="text-base font-semibold">{money(v.trade_price_per_sqft)}</span><span className="text-xs text-muted-foreground">/sq ft</span></>}
                <span className="ml-1.5 text-xs text-muted-foreground">({money(v.trade_price)}/box)</span>
              </span>
            ) : <span className="text-xs text-muted-foreground" data-testid={`trade-pending-${v.sku}`}>Price on request</span>}
          </div>
          {v.margin_pct != null && (
            <div className="flex justify-between" data-testid={`margin-row-${v.sku}`}><span className="text-muted-foreground">Estimated gross margin</span><span className="font-medium text-status-available">{v.margin_pct}%</span></div>
          )}
        </>
      )}
    </div>
  );
}

// ID-061: one card for zellige variants and mosaic variations — all values come from the canonical catalog payload
export function CatalogCard({ v, customerView, notifyMe, onNotify, onOpen }) {
  const mosaic = v.category === "mosaic";
  const title = mosaic ? v.collection : v.label;
  return (
    <Card data-testid={`catalog-card-${v.sku}`} onClick={onOpen}
      className="cursor-pointer overflow-hidden border-border bg-white shadow-[0_2px_12px_rgba(26,26,24,0.04)] transition-[transform,box-shadow] duration-200 hover:-translate-y-[2px] hover:shadow-[0_6px_20px_rgba(26,26,24,0.08)]">
      {v.image_urls?.[0] ? (
        <img src={v.image_urls[0]} alt={`${title} ${v.sku}`} loading="lazy" className="h-48 w-full object-cover" data-testid={`catalog-card-image-${v.sku}`} />
      ) : (
        <div className="flex h-48 flex-col items-center justify-center bg-sand px-4 text-center" data-testid={`catalog-card-noimage-${v.sku}`}>
          <span className="font-display text-2xl font-light text-warmgrey" data-testid="catalog-card-color">{mosaic ? compositionText(v) : v.display_color_name || v.color_name}</span>
          <span className="mt-1 text-[11px] text-muted-foreground">Photo coming soon</span>
        </div>
      )}
      <CardContent className="p-6">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta">{mosaic ? `Mosaic${v.collection_family && v.collection_family !== "Mosaic" ? ` · ${v.collection_family}` : ""}` : v.product_type}</p>
        <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
          <p className="font-display text-xl font-medium text-charcoal">{title}</p>
          <span className="rounded border border-limestone bg-sand px-1.5 py-0.5 font-mono text-[10px] font-semibold text-charcoal" data-testid={`sku-chip-${v.sku}`}>{v.sku}</span>
        </div>
        {mosaic ? (
          <p className="mt-1 text-xs text-charcoal" data-testid={`composition-${v.sku}`}>{compositionText(v)}</p>
        ) : (v.display_color_name && v.color_name && !String(title || "").includes(v.color_name) && <p className="mt-1 text-[11px] text-muted-foreground" data-testid={`factory-name-${v.sku}`}>Factory name: {v.color_name}</p>)}
        <p className="mt-1 text-xs text-muted-foreground" data-testid={`dims-${v.sku}`}>{v.category === "mosaic" ? `${v.mesh_sheet_approx ? `Mesh sheet ${v.mesh_sheet_approx}` : "Mesh sheet size not set"}${v.sqft_per_sheet != null ? ` · ${v.sqft_per_sheet} sq ft/sheet` : ""}` : dimsLine(v)} · {v.box_coverage_sqft} sq ft/box</p>
        {v.matched_terms?.length > 0 && <p className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground" data-testid={`matched-terms-${v.sku}`}>{v.matched_terms.join(" · ")}</p>}
        <Availability v={v} customerView={customerView} notifyMe={notifyMe} onNotify={onNotify} />
        <Prices v={v} customerView={customerView} />
      </CardContent>
    </Card>
  );
}
